import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import { SCREEN_PREFERENCE_CODES } from '@/routes';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import {
  assignedEmployeesListService,
  type AssignedEmployee,
  type AssignedEmployeesStatusFilter,
  ASSIGNED_EMPLOYEES_LIST_DEFAULT_PAGE_SIZE,
  ASSIGNED_EMPLOYEES_STATUS_FILTER,
} from '@/api/domains/securityGrouping';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';


interface AssignedEmployeesFiltersState {
  status: AssignedEmployeesStatusFilter;
}

export interface UseAssignedEmployeesListResult {
  rows: AssignedEmployee[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  filters: AssignedEmployeesFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<AssignedEmployeesFiltersState>>;
  filterConfig: SimpleFilter[];
  columns: TableColumn<AssignedEmployee>[];
  actions: TableAction<AssignedEmployee>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  canView: boolean;
}

export function useAssignedEmployeesList(): UseAssignedEmployeesListResult {
  const { t: translate } = useTranslation();
  const { showApiError, openSidePanel } = useUI();
  const { isLoaded, canView } = usePagePermissions();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<AssignedEmployeesFiltersState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<AssignedEmployee> | null> => {
      const response = await assignedEmployeesListService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          status: params.filters.status,
          orderBy: params.sort?.key ?? null,
          orderDirection: params.sort?.direction ?? null,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('securityGrouping:assignedEmployees.messages.loadError'));
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
  } = usePaginatedList<AssignedEmployee, AssignedEmployeesFiltersState>({
    fetchFn,
    initialFilters: { status: ASSIGNED_EMPLOYEES_STATUS_FILTER.ALL },
    initialPageSize: ASSIGNED_EMPLOYEES_LIST_DEFAULT_PAGE_SIZE,
    enabled: isLoaded && canView,
    orderByMap: {
      rut: 'employeeTaxIdentificationNumber',
      name: 'employeeName',
      adminGroupsCount: 'adminGroupsCount',
      memberGroupsCount: 'memberGroupsCount',
      status: 'status',
    },
    screenCode: SCREEN_PREFERENCE_CODES.AGRUPACIONES_ASSIGNED_EMPLOYEES,
  });

  const filterConfig: SimpleFilter[] = useMemo(() => [
    {
      key: 'status',
      label: translate('securityGrouping:assignedEmployees.filters.status'),
      options: [
        { value: ASSIGNED_EMPLOYEES_STATUS_FILTER.ALL, label: translate('securityGrouping:assignedEmployees.filters.all') },
        { value: ASSIGNED_EMPLOYEES_STATUS_FILTER.ENABLED, label: translate('securityGrouping:assignedEmployees.filters.enabled') },
        { value: ASSIGNED_EMPLOYEES_STATUS_FILTER.DISABLED, label: translate('securityGrouping:assignedEmployees.filters.disabled') },
      ],
    },
  ], [translate]);

  const columns: TableColumn<AssignedEmployee>[] = useMemo(
    () => [
      {
        key: 'rut',
        header: translate('securityGrouping:assignedEmployees.columns.rut'),
        render: (row: AssignedEmployee) => row.rut,
        width: 130,
        sortable: true,
      },
      {
        key: 'name',
        header: translate('securityGrouping:assignedEmployees.columns.name'),
        render: (row: AssignedEmployee) => row.name,
        sortable: true,
      },
      {
        key: 'adminGroupsCount',
        header: translate('securityGrouping:assignedEmployees.columns.adminGroupsCount'),
        render: (row: AssignedEmployee) => row.adminGroupsCount,
        width: 150,
        align: 'center',
        sortable: true,
      },
      {
        key: 'memberGroupsCount',
        header: translate('securityGrouping:assignedEmployees.columns.memberGroupsCount'),
        render: (row: AssignedEmployee) => row.memberGroupsCount,
        width: 150,
        align: 'center',
        sortable: true,
      },
      {
        key: 'status',
        header: translate('securityGrouping:assignedEmployees.columns.status'),
        render: (row: AssignedEmployee) => (
          <Text
            variant="body-regular"
            style={{
              color: row.status === 'Vigente' ? colors.feedback.success400 : colors.grays.neutral99,
              fontWeight: 500,
            }}
          >
            {row.status}
          </Text>
        ),
        width: 110,
        sortable: true,
      },
    ],
    [translate],
  );

  const handleViewDetail = useCallback(
    (row: AssignedEmployee) => {
      openSidePanel({
        component: 'AssignedEmployeeDetailPanel',
        title: translate('securityGrouping:assignedEmployees.detail.title'),
        props: { employee: row },
      });
    },
    [openSidePanel, translate],
  );

  const actions: TableAction<AssignedEmployee>[] = useMemo(
    () => [
      {
        id: 'view-detail',
        label: translate('common:tableActions.detail'),
        icon: 'eye',
        onClick: handleViewDetail,
      },
    ],
    [translate, handleViewDetail],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    canView,
  };
}
